import type {
  FocusAccent,
  FocusArea,
  Observation,
  SchoolTeamSnapshot,
} from "@/config/teamsSample";
import type { ChatEntry } from "@/types/chat";

/** Focus cards only surface when this share of active staff shows the pattern. */
export const SCHOOL_TEAM_PRIVACY_THRESHOLD_PCT = 25;

/** Rolling window for "this semester" activity in aggregations. */
export const SCHOOL_TEAM_ACTIVITY_WINDOW_DAYS = 120;

export type StaffSessionActivity = {
  userId: string;
  chatMode: ChatEntry;
};

type FocusDefinition = {
  id: string;
  chatModes: ChatEntry[];
  label: string;
  category: string;
  accent: FocusAccent;
  /** Primary chat entry for "Talk it through" coaching seed. */
  chatEntry: ChatEntry;
};

const FOCUS_DEFINITIONS: FocusDefinition[] = [
  {
    id: "reset-nervous-system",
    chatModes: ["reset_my_nervous_system"],
    label: "Reset my Nervous System",
    category: "Self-regulation",
    accent: "self_regulation",
    chatEntry: "reset_my_nervous_system",
  },
  {
    id: "parent-conversations",
    chatModes: ["strengthen_my_voice", "connect_with_students", "lead_with_clarity"],
    label: "Difficult Conversations",
    category: "Conversation Lab",
    accent: "conversation_lab",
    chatEntry: "strengthen_my_voice",
  },
  {
    id: "reclaim-my-balance",
    chatModes: ["reclaim_my_balance"],
    label: "Reclaim my Balance",
    category: "Restoration",
    accent: "restoration",
    chatEntry: "reclaim_my_balance",
  },
  {
    id: "expand-my-impact",
    chatModes: ["expand_my_impact", "get_coaching", "ai_mentor"],
    label: "Expand my Impact",
    category: "Growth",
    accent: "growth",
    chatEntry: "expand_my_impact",
  },
];

export function currentSemesterLabel(date = new Date()): string {
  const month = date.getMonth();
  const year = date.getFullYear();
  // Jan–Jun Spring; Jul–Dec Fall (aligned with K–12 terms).
  return month <= 5 ? `Spring ${year}` : `Fall ${year}`;
}

export function activityWindowStartIso(date = new Date()): string {
  const start = new Date(date);
  start.setDate(start.getDate() - SCHOOL_TEAM_ACTIVITY_WINDOW_DAYS);
  return start.toISOString();
}

function uniqueHelperCount(activities: StaffSessionActivity[]): number {
  return new Set(activities.map((a) => a.userId)).size;
}

function helpersUsingAnyMode(activities: StaffSessionActivity[], modes: ChatEntry[]): number {
  const modeSet = new Set(modes);
  const users = new Set<string>();
  for (const row of activities) {
    if (modeSet.has(row.chatMode)) users.add(row.userId);
  }
  return users.size;
}

function buildFocusAreas(activities: StaffSessionActivity[]): FocusArea[] {
  const helpers = uniqueHelperCount(activities);
  if (helpers === 0) return [];

  const areas: FocusArea[] = [];
  for (const def of FOCUS_DEFINITIONS) {
    const count = helpersUsingAnyMode(activities, def.chatModes);
    const percent = Math.round((count / helpers) * 100);
    if (percent < SCHOOL_TEAM_PRIVACY_THRESHOLD_PCT) continue;
    areas.push({
      id: def.id,
      chatEntry: def.chatEntry,
      label: def.label,
      category: def.category,
      accent: def.accent,
      percent,
    });
  }

  return areas.sort((a, b) => b.percent - a.percent);
}

function buildObservations(
  focusAreas: FocusArea[],
  helperCount: number
): Observation[] {
  if (helperCount === 0 || focusAreas.length === 0) return [];

  const observations: Observation[] = [];
  const top = focusAreas[0]!;
  const second = focusAreas[1];

  observations.push({
    id: "top-focus-area",
    direction: "up",
    body: `${top.percent}% of staff with HeyBodhi activity this semester are reaching for ${top.label.toLowerCase()} — your building's strongest collective signal right now.`,
    tags: [
      { label: top.category, tone: "neutral" },
      { label: "Positive", tone: "positive" },
    ],
  });

  if (second && second.percent >= SCHOOL_TEAM_PRIVACY_THRESHOLD_PCT) {
    observations.push({
      id: "dual-focus",
      direction: "right",
      body: `${top.label} and ${second.label} are where staff are spending the most time — worth noticing whether that matches what you're seeing on the ground, or whether people are carrying strain in silence.`,
      tags: [
        { label: "Relational", tone: "neutral" },
        { label: "Worth Exploring", tone: "worth_exploring" },
      ],
    });
  }

  if (helperCount >= 5) {
    observations.push({
      id: "building-momentum",
      direction: "up",
      body: `${helperCount} staff members have active HeyBodhi conversations this semester — enough signal to see building-level patterns without exposing anyone's private sessions.`,
      tags: [
        { label: "Momentum", tone: "positive" },
        { label: "Aggregate", tone: "neutral" },
      ],
    });
  }

  return observations.slice(0, 3);
}

export function aggregateSchoolTeamSnapshot(params: {
  schoolId: string;
  schoolName: string;
  activities: StaffSessionActivity[];
  semesterLabel?: string;
}): SchoolTeamSnapshot {
  const helperCount = uniqueHelperCount(params.activities);
  const focusAreas = buildFocusAreas(params.activities);
  const observations = buildObservations(focusAreas, helperCount);

  return {
    schoolId: params.schoolId,
    schoolName: params.schoolName,
    tagline: "a mirror held up to the whole building, without naming anyone inside it.",
    semesterLabel: params.semesterLabel ?? currentSemesterLabel(),
    helperCount,
    focusAreas,
    observations,
  };
}

export function emptySchoolTeamSnapshot(schoolId: string, schoolName: string): SchoolTeamSnapshot {
  return {
    schoolId,
    schoolName,
    tagline: "a mirror held up to the whole building, without naming anyone inside it.",
    semesterLabel: currentSemesterLabel(),
    helperCount: 0,
    focusAreas: [],
    observations: [],
  };
}
